import { cn } from 'lib/helpers/utility';

const toneClassName = {
  danger: 'text-danger-600',
  yellow: 'text-yellow-400',
  success: 'text-success-500'
};

// Color thresholds: under 20% is critical (red), under 50% is low (yellow),
// otherwise healthy (green).
const getTone = (percentage) => {
  if (percentage < 20) return 'danger';
  if (percentage < 50) return 'yellow';
  return 'success';
};

/**
 * A small circular battery gauge: a ring filled proportionally to the battery
 * percentage with the value in the center. When the device isn't connected (or
 * has no reading) the ring is greyed out and shows a "~" placeholder.
 */
const BatteryRing = ({ percentage, connected = true, charging = false, size = 30, className }) => {
  const strokeWidth = Math.max(3, Math.round(size / 9));
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  const hasValue = connected && Number.isFinite(percentage);
  const clamped = hasValue ? Math.max(0, Math.min(100, percentage)) : 0;
  const tone = hasValue ? getTone(clamped) : null;
  const strokeDashoffset = circumference - (clamped / 100) * circumference;

  const label = hasValue ? `${Math.round(clamped)}` : '~';
  const ariaLabel = hasValue
    ? `Battery ${Math.round(clamped)}%${charging ? ', charging' : ''}`
    : 'Battery not connected';

  return (
    <div
      className={cn(
        'relative flex shrink-0 items-center justify-center rounded-full bg-white',
        className
      )}
      style={{ width: size, height: size }}
      role="img"
      aria-label={ariaLabel}
      title={ariaLabel}>
      <svg
        className="absolute inset-0"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          className="text-neutral-200"
          stroke="currentColor"
          fill="none"
        />
        {hasValue && clamped > 0 && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={`${circumference} ${circumference}`}
            strokeDashoffset={strokeDashoffset}
            className={cn(
              'transition-[stroke-dashoffset] duration-500 ease-out',
              toneClassName[tone]
            )}
            stroke="currentColor"
            fill="none"
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        )}
      </svg>
      <span
        className={cn(
          'font-600 leading-none',
          size <= 30 ? 'text-[10px]' : 'text-[12px]',
          hasValue ? 'text-neutral-900' : 'text-neutral-400'
        )}>
        {label}
      </span>
    </div>
  );
};

export default BatteryRing;
